import { ROUTE } from '../../../../routes';
import { SLOT_SPECS, logoSpec, type LogoStepState } from '../../../casino-settings/logos';
import { pickChip, type WebsiteCardBody, type WebsiteCardIntent, type WebsiteCardSummary } from '../../card-summary';
import type { ReadStatus } from '../creatives/creatives-summary';

/**
 * What the Logos card says, from what its hook read: the logo family (or the brand record's map), the brand's logo
 * deploy step, the replacement run this browser left in flight, and the logo-edit lock (the 50-player rule, from the
 * player roster). Pure: copy is an id, mapped to its key in `logos-copy.ts`.
 */

/**
 * The logo-edit lock (the Logos page's 50-player rule): `pending` until the player count is in, `unknown` when it could
 * not be read. Unknown is not Locked: the Logos page withholds its count copy then, and only keeps replacing shut.
 */
export type LogoLock = 'pending' | 'editable' | 'locked' | 'unknown';

export type LogosCardInput = {
  /** The logo family read. */
  status: ReadStatus;
  /**
   * `mock` is the schematic fallback: the brand has no real logos yet, and `slots` is empty then. Otherwise the brand's
   * logo slots in the Logos page's order, its primary logo first.
   */
  logos: { source: 'brand' | 'mock'; slots: string[] };
  /**
   * The brand's `prepare_logo_assets` deploy step: a load-time snapshot of the brand record that nothing refreshes in a
   * session. Read only while there are no real logos, as the Logos page reads it (its placeholder's "being generated").
   */
  step: LogoStepState;
  /** The stored replacement run, read once: still going, ended without replacing anything, or none to report. */
  run: 'none' | 'running' | 'failed';
  lock: LogoLock;
};

export type LogosCopy =
  | { id: 'failed' }
  | { id: 'generating' }
  | { id: 'locked' }
  | { id: 'last-change-failed' }
  | { id: 'all-logos-ready'; n: number }
  | { id: 'logos-ready'; n: number }
  | { id: 'preparing-logos' }
  | { id: 'no-logos' }
  | { id: 'open-logos' }
  | { id: 'download-all' }
  | { id: 'replace-logo' };

const OPEN_LOGOS: WebsiteCardIntent<LogosCopy> = { copy: { id: 'open-logos' }, to: ROUTE.logos() };

/** The logo slots the Logos page knows (and sizes), the favicon included: the "9" of "All 9 logos ready". */
const KNOWN_SLOTS = Object.keys(SLOT_SPECS).length;

export const deriveLogosSummary = (input: LogosCardInput): WebsiteCardSummary<LogosCopy> => {
  const { status, logos, step, run, lock } = input;
  // As on the Logos page: with real logos only the run it follows counts, since a stale step would pin the card to
  // Generating (and hide Replace logo) for the session; the page shows no failed step at all, so neither does the card.
  const problem = run === 'failed';
  const working = run === 'running' || (logos.source === 'mock' && step === 'generating');
  const locked = lock === 'locked';
  const chip = pickChip<LogosCopy>([
    problem ? { tone: 'problem', copy: { id: 'failed' } } : null,
    working ? { tone: 'working', copy: { id: 'generating' } } : null,
    locked ? { tone: 'neutral', copy: { id: 'locked' } } : null,
  ]);
  const only = (body: WebsiteCardBody<LogosCopy>): WebsiteCardSummary<LogosCopy> => ({
    chip,
    body,
    primary: OPEN_LOGOS,
    secondary: null,
  });

  if (status === 'loading') return only({ kind: 'loading' });
  if (status === 'failed') return only({ kind: 'failed' });
  if (problem) return only({ kind: 'waiting', lines: [{ id: 'last-change-failed' }] });
  // The schematic fallback reads as a finished brand, so it is never counted or offered as a download.
  if (logos.source === 'mock') {
    return only({ kind: 'get-started', line: { id: working ? 'preparing-logos' : 'no-logos' } });
  }

  // Only the known slots are counted, so "All 9 logos ready" and "N logos ready" stay true beside an extra slot.
  const n = logos.slots.filter((slot) => logoSpec(slot) !== undefined).length;
  const [primarySlot] = logos.slots;
  return {
    chip,
    body: {
      kind: 'context',
      line: n === KNOWN_SLOTS ? { id: 'all-logos-ready', n } : { id: 'logos-ready', n },
    },
    primary: { copy: { id: 'download-all' }, run: 'download-all' },
    // Replacing is offered only while the page would offer it: the lock known and open, and nothing being redrawn. It
    // opens the upload for the brand's own primary logo, the one the page heads with (not always the pipeline's base).
    secondary:
      lock === 'editable' && !working && primarySlot !== undefined
        ? { copy: { id: 'replace-logo' }, to: ROUTE.logosReplace(primarySlot) }
        : null,
  };
};
